import { motion } from "framer-motion";
import { MapPin, Clock, Phone, ArrowRight, ShoppingBag, Facebook } from "lucide-react";
import { useLang } from "@/i18n";
import { WHATSAPP_URL, WhatsAppIcon } from "@/components/WhatsApp";

export const LOGO_URL =
  "https://customer-assets-v7afamib.emergentagent.net/job_gym-packages-1/artifacts/3584d60ca0410177_ChatGPT%20Image%20Sep%2019%2C%202026%2C%2005_00_46%20PM.png";

const Footer = ({ onEnroll }) => {
  const { t } = useLang();
  return (
    <footer id="contact" className="relative border-t border-zinc-800 bg-surface/60" data-testid="footer">
      <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-16 flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center"
        >
          <h2 className="max-w-2xl font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-white sm:text-5xl" data-testid="cta-title">
            {t.cta_title}
          </h2>
          <div className="shrink-0">
            <p className="mb-4 max-w-xs text-sm text-zinc-400">{t.cta_sub}</p>
            <button
              data-testid="cta-enroll-button"
              onClick={() => onEnroll(null)}
              className="group flex items-center gap-2.5 rounded-full bg-volt px-8 py-4 font-display text-base font-bold uppercase tracking-widest text-ink transition-all duration-200 hover:bg-volt-hover hover:scale-[1.04]"
            >
              {t.enroll}
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" />
            </button>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-10 border-t border-zinc-800 pt-12 md:grid-cols-3">
          <div>
            <img src={LOGO_URL} alt="Fitnastic Gym" className="w-56 rounded-lg" data-testid="footer-logo" />
            <p className="mt-4 max-w-xs text-sm text-zinc-500">{t.footer_tagline}</p>
          </div>
          <div>
            <h4 className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-volt" data-testid="footer-hours-title">{t.footer_hours_title}</h4>
            <p className="flex items-center gap-2.5 text-sm text-zinc-400"><Clock className="h-4 w-4 text-zinc-600" />{t.footer_hours}</p>
            <p className="mt-2 flex items-center gap-2.5 text-sm text-zinc-400"><Clock className="h-4 w-4 text-zinc-600" />{t.footer_hours2}</p>
          </div>
          <div>
            <h4 className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-volt" data-testid="footer-contact-title">{t.footer_contact_title}</h4>
            <p className="flex items-start gap-2.5 text-sm text-zinc-400"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-zinc-600" />{t.footer_address}</p>
            <p className="mt-2 flex items-center gap-2.5 text-sm text-zinc-400"><Phone className="h-4 w-4 text-zinc-600" />8918007538</p>
            <p className="mt-2 flex items-center gap-2.5 text-sm text-zinc-400"><Phone className="h-4 w-4 text-zinc-600" />9064906568</p>
            <a
              data-testid="footer-whatsapp-link"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-2.5 rounded-full border border-[#25D366]/40 bg-[#25D366]/10 px-4 py-2 text-sm font-semibold text-[#25D366] transition-colors duration-200 hover:bg-[#25D366] hover:text-ink"
            >
              <WhatsAppIcon className="h-4 w-4" />
              {t.whatsapp_chat}
            </a>
          </div>
        </div>

        <div className="mt-12 overflow-hidden rounded-2xl border border-zinc-800" data-testid="map-embed">
          <iframe
            title="Fitnastic Gym location map"
            data-testid="map-iframe"
            src="https://maps.google.com/maps?q=Fitnastic%20gym%20unit2%2C%20Kantapukur%2C%20Bardhaman%2C%20West%20Bengal%20713104&output=embed"
            className="h-72 w-full grayscale invert-[0.92] hue-rotate-180 contrast-[0.9]"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-zinc-800 bg-panel px-5 py-4">
            <p className="flex items-center gap-2 text-sm text-zinc-400">
              <MapPin className="h-4 w-4 text-volt" />
              {t.footer_address}
            </p>
            <a
              data-testid="get-directions-button"
              href="https://maps.app.goo.gl/mC4DSfXoRwdusBiX9"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full border border-volt/50 px-5 py-2 font-display text-xs font-bold uppercase tracking-widest text-volt transition-colors duration-200 hover:bg-volt hover:text-ink"
            >
              {t.get_directions}
            </a>
          </div>
        </div>

        <div className="mt-8 overflow-hidden rounded-2xl border border-zinc-800" data-testid="shop-map-embed">
          <div className="flex items-center gap-2.5 border-b border-zinc-800 bg-panel px-5 py-4">
            <ShoppingBag className="h-5 w-5 text-volt" />
            <p className="font-display text-base font-bold uppercase tracking-widest text-white" data-testid="footer-shop-title">{t.shop_title}</p>
          </div>
          <iframe
            title="Fitnastic Gymm Equipments shop location map"
            data-testid="shop-map-iframe"
            src="https://maps.google.com/maps?q=Fitnastic%20gymm%20equipments%2C%20Khagragarh%20more%2C%20Bardhaman%2C%20West%20Bengal%20713104&output=embed"
            className="h-72 w-full grayscale invert-[0.92] hue-rotate-180 contrast-[0.9]"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-zinc-800 bg-panel px-5 py-4">
            <p className="flex items-center gap-2 text-sm text-zinc-400" data-testid="footer-shop-address">
              <MapPin className="h-4 w-4 text-volt" />
              {t.shop_address}
            </p>
            <a
              data-testid="shop-directions-button"
              href="https://maps.app.goo.gl/2cd3snEkAZvxYjDN8"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full border border-volt/50 px-5 py-2 font-display text-xs font-bold uppercase tracking-widest text-volt transition-colors duration-200 hover:bg-volt hover:text-ink"
            >
              {t.get_directions}
            </a>
          </div>
        </div>

        <div className="mt-14 flex flex-wrap items-center justify-between gap-4 border-t border-zinc-800 pt-8">
          <p className="font-mono text-xs uppercase tracking-widest text-zinc-600" data-testid="footer-copyright">
            © {new Date().getFullYear()} Fitnastic Gym — {t.footer_rights}
          </p>
          <div className="flex items-center gap-3" data-testid="footer-social">
            <span className="font-mono text-xs uppercase tracking-widest text-zinc-600">{t.footer_follow}</span>
            <a
              data-testid="footer-facebook-link"
              href="https://www.facebook.com/share/1CMSaDjxe4/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Fitnastic Gym on Facebook"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-zinc-700 text-zinc-400 transition-all duration-200 hover:border-[#1877F2] hover:bg-[#1877F2]/10 hover:text-[#1877F2]"
            >
              <Facebook className="h-4.5 w-4.5" />
            </a>
          </div>
          <p className="font-mono text-xs uppercase tracking-widest text-zinc-700">Since 2015 / West Bengal / India</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
